import { isLockFieldValue } from 'components/batchForm/BatchForm.helpers';
import {
  amountFieldFormatter,
  customizableTenorFieldsFormatter,
  dealSplitGridMetadataFormatter,
  dealSplitGridMetadataValueValidator,
  type FieldMetadata,
  singleCustodianDealSplitFieldMetadataFormatter,
  singleCustodianDealSplitFieldMetadataValidator,
} from 'models/forms/fields';
import { availableItemsFormatter, availableItemsValidator } from 'models/forms/fields/AvailableItemsField';
import {
  availableStrikeModeFieldFormatter,
  strikeModeFieldValueValidator,
} from 'models/forms/fields/AvailableStrikeModeFieldBase';
import { FieldMetadataBase } from 'models/forms/fields/FieldMetadataBase';
import { multipleUnderlyingsFieldValueEnricher } from 'models/forms/fields/MultipleUnderlyingsFieldMetadata';
import { radioButtonsFieldFormatter } from 'models/forms/fields/RadioButtonsFieldMetadata';
import type { FieldFormatter, FieldValueEnricher, FieldValueValidator } from 'models/forms/fields/utils';
import type { FieldMetadataById } from 'state/common-form/metaData/fields/fields.model';
import type { UpdateAction } from 'state/formData';
import type { ProductNode } from 'state/product-description/model';
import { getBatchFormFieldMetadata, getSelectedFormsProductSubtypes } from 'state/selectors';
import type { AppState } from 'state/state.model';

const excludedFieldTypes = ['CurrencyPairSwitcherField' as FieldMetadata['type'], 'Blank' as FieldMetadata['type']];
const excludedFieldsIds = ['ProductSubtype', 'ScheduleDatesModal', 'ExerciseScheduleModal', 'SwapScheduleModal'];

const fieldValueValidators: FieldValueValidator<any, any>[] = [
  dealSplitGridMetadataValueValidator,
  strikeModeFieldValueValidator,
  availableItemsValidator,
  singleCustodianDealSplitFieldMetadataValidator,
];

const defaultFieldFormatter: FieldFormatter<FieldMetadataBase<string>> = {
  areSameType: () => true,
  merge: (first, second) => ({ ...first, ...second }),
  withDefault: field => field,
  isHandled: () => true,
};

const fieldsFormatters: FieldFormatter<any>[] = [
  radioButtonsFieldFormatter,
  amountFieldFormatter,
  availableStrikeModeFieldFormatter,
  customizableTenorFieldsFormatter,
  dealSplitGridMetadataFormatter,
  availableItemsFormatter,
  singleCustodianDealSplitFieldMetadataFormatter,
  defaultFieldFormatter,
];

export const XMarginFieldValueEnricher: FieldValueEnricher<any> = {
  isHandled: ({ id, type }) => id === 'XMargin' && type === 'PercentField',
  generateEnrichment(_action, _target, changes) {
    return isLockFieldValue(changes) ? changes.value : changes;
  },
};

const fieldsEnrichers: FieldValueEnricher<any>[] = [multipleUnderlyingsFieldValueEnricher, XMarginFieldValueEnricher];

export function isFieldMetadataUsableInMultipleApply(fieldMetadata?: FieldMetadata) {
  return (
    !!fieldMetadata &&
    !fieldMetadata.isHidden &&
    !fieldMetadata.isReadOnly &&
    !excludedFieldTypes.includes(fieldMetadata.type) &&
    !excludedFieldsIds.includes(fieldMetadata.id)
  );
}

export function isFieldValueValid(value: unknown, fieldMetadata: FieldMetadata) {
  for (const validator of fieldValueValidators) {
    if (validator.isHandled(fieldMetadata) && !validator.validateValue(value, fieldMetadata)) {
      return false;
    }
  }
  return true;
}

export function mergeFieldsMetadata(
  fieldMetadataById: FieldMetadataById,
  fieldName: string,
  newField: FieldMetadata,
): FieldMetadataById {
  const previousField = fieldMetadataById[fieldName];
  let formattedField = newField;

  for (const fieldFormatter of fieldsFormatters) {
    if (fieldFormatter.isHandled(newField) && fieldFormatter.format) {
      formattedField = fieldFormatter.format(newField);
    }
    if (previousField && fieldFormatter.areSameType?.(previousField, newField) && fieldFormatter.merge) {
      return { ...fieldMetadataById, [fieldName]: fieldFormatter.merge(previousField, formattedField) };
    }

    if (!previousField && fieldFormatter.isHandled(newField) && fieldFormatter.withDefault) {
      return { ...fieldMetadataById, [fieldName]: fieldFormatter.withDefault(formattedField) };
    }
  }
  return { ...fieldMetadataById, [fieldName]: formattedField };
}

export function generateEnrichmentForField(
  field: FieldMetadata | undefined,
  action: UpdateAction | undefined,
  changes: unknown,
  target: unknown,
): unknown {
  if (field === undefined || action === undefined) {
    return;
  }
  for (const enricher of fieldsEnrichers) {
    if (enricher.isHandled(field)) {
      return enricher.generateEnrichment(action, target, changes);
    }
  }
}
export const modifyFieldTypeBasedOnId = (productNode: ProductNode, state: AppState, tabId: string) => {
  if (productNode.id === 'XMargin') {
    return generateModifiedXMarginNode(productNode, state, tabId);
  }

  return productNode;
};

export function generateModifiedXMarginNode(productNode: ProductNode, state: AppState, tabId: string): ProductNode {
  const { formsIds } = getSelectedFormsProductSubtypes(state, tabId);
  const allFieldsMetadata = formsIds.map(formId => getBatchFormFieldMetadata(state, formId));
  return allFieldsMetadata.some(n => n['XMargin']?.type === 'LockablePercentField')
    ? { ...productNode, type: 'LockablePercentField' }
    : { ...productNode, type: 'PercentField' };
}
